import { Match, Show, Switch, createSignal, onMount } from "solid-js";
import { HttpError } from "~/lib/http";
import { AuditView } from "./AuditView";
import { LoginView } from "./LoginView";
import { RegistrationsView } from "./RegistrationsView";
import { SchoolsView } from "./SchoolsView";
import { SystemView } from "./SystemView";
import { TeachersView } from "./TeachersView";
import type { AdminApi, AdminInfo } from "./api";

type View = "registrations" | "teachers" | "schools" | "audit" | "system";

const VIEWS: Array<{ id: View; label: string }> = [
  { id: "registrations", label: "Anfragen" },
  { id: "schools", label: "Schulen" },
  { id: "teachers", label: "Lehrkräfte" },
  { id: "audit", label: "Protokoll" },
  { id: "system", label: "System" }
];

export function AdminApp(props: { api: AdminApi }) {
  const [admin, setAdmin] = createSignal<AdminInfo | null>(null);
  const [ready, setReady] = createSignal(false);
  const [view, setView] = createSignal<View>("registrations");
  const [notice, setNotice] = createSignal("");

  onMount(async () => {
    try {
      setAdmin(await props.api.me());
    } catch {
      setAdmin(null);
    }
    setReady(true);
  });

  const guard = async <T,>(work: () => Promise<T>): Promise<T> => {
    try {
      return await work();
    } catch (cause) {
      if (cause instanceof HttpError && cause.status === 401) {
        setAdmin(null);
        setNotice("Die Sitzung ist abgelaufen. Bitte melde dich neu an.");
      }
      throw cause;
    }
  };

  const logout = async () => {
    try { await props.api.logout(); } catch { /* Sitzung ist ohnehin weg */ }
    setAdmin(null);
    setNotice("");
  };

  const loggedIn = (info: AdminInfo, recoveryCodesLeft?: number) => {
    setAdmin(info);
    setView("registrations");
    setNotice(recoveryCodesLeft === undefined ? "" : `Du hast dich mit einem Notfall-Code angemeldet. Es sind noch ${recoveryCodesLeft} Notfall-Codes übrig. Richte den Zwei-Faktor-Login am Server neu ein.`);
  };

  return (
    <Show when={ready()} fallback={<div class="adm-center">Wird geladen …</div>}>
      <Show when={admin()} fallback={<LoginView api={props.api} notice={notice()} onLoggedIn={loggedIn} />}>
        {(current) => (
          <>
            <header class="adm-top">
              <strong>Reflexo Admin</strong>
              <nav class="adm-nav" aria-label="Bereiche">
                {VIEWS.map((entry) => (
                  <button type="button" aria-current={view() === entry.id ? "page" : undefined} onClick={() => setView(entry.id)}>{entry.label}</button>
                ))}
              </nav>
              <div class="adm-who"><span>{current().displayName}</span><button class="adm-btn small" type="button" onClick={() => void logout()}>Abmelden</button></div>
            </header>
            <main class="adm-main">
              <Show when={notice()}><div class="adm-info" role="status">{notice()}</div></Show>
              <Switch>
                <Match when={view() === "registrations"}><RegistrationsView api={props.api} admin={current()} guard={guard} /></Match>
                <Match when={view() === "schools"}><SchoolsView api={props.api} guard={guard} /></Match>
                <Match when={view() === "teachers"}><TeachersView api={props.api} guard={guard} /></Match>
                <Match when={view() === "audit"}><AuditView api={props.api} guard={guard} /></Match>
                <Match when={view() === "system"}><SystemView api={props.api} guard={guard} /></Match>
              </Switch>
            </main>
          </>
        )}
      </Show>
    </Show>
  );
}
